import { TFlat } from "../../../../shared/types/flat";


/**
 * Sets the fill style of an SVG element with a specified color and opacity.
 * @param {SVGElement} element - The SVG element to style.
 * @param {string} color - The fill color to apply to the element.
 * @param {string} [opacity='1'] - The fill opacity to apply, defaulting to '1'.
 * @returns {void}
*/
const setFillStyle = (element: SVGElement, color: string, opacity: string = '1'): void => {
    element.style.fill = color;
    element.style.fillOpacity = opacity;
};

/**
 * Removes the 'selected' class and resets the fill style on each Flat element.
 * @param {NodeListOf<Element>} flatElements - The list of SVG elements representing Flat elements.
 * @returns {void}
*/
const removeSelected = (flatElements: NodeListOf<Element>): void => {
    flatElements.forEach((el) => {
        el.classList.remove('selected');
        setFillStyle(el as SVGElement, '', '0.01');
    });
}

/**
 * Attaches a click event listener to each Flat element.
 * When clicked, the element is styled as selected, and a callback function is triggered with the Flat name.
 * @param {NodeListOf<Element>} flatElements - The list of SVG Flat elements.
 * @param {(flatName: string) => void} onElementClick - Callback function triggered with the Flat's name upon selection.
 * @returns {void}
*/
export const attachFlatClickEvent = (flatElements: NodeListOf<Element>, onElementClick: (flatName: string) => void): void => {
    flatElements.forEach((element) => {
      const svgElement = element as SVGElement;
      const flatId = svgElement.getAttribute('id');

      svgElement.addEventListener('click', () => {
        removeSelected(flatElements);
          svgElement.classList.add('selected');
          setFillStyle(svgElement, 'rgba(255, 192, 203, 0.5)', '1');

          if (flatId) {
              onElementClick(flatId);
          }
      });
  });
};

/**
 * Attaches hover event listeners to each Flat element.
 * When hovered, the corresponding Flat is fetched and passed to a callback function.
 * @param {NodeListOf<Element>} flatsElements - The list of SVG Flat elements.
 * @param {TFlat[]} contextFlats - The list of Flats.
 * @param {(flat: any) => void} onElementHover - Callback function triggered with the Flat data on hover.
 * @returns {void}
*/
export const attachFlatHoverEvent = (flatsElements: NodeListOf<Element>, contextFlats: TFlat[], onElementHover: (flat: any) => void): void => {
    flatsElements.forEach((element) => {
        element.addEventListener('mouseenter', () => {
            const flatId = element.getAttribute('id');
            if (flatId) {
                const hoveredFlat = contextFlats.find((flat) => flat.name === flatId);
                if (hoveredFlat) {
                    onElementHover(hoveredFlat);
                }
            }
        });

        element.addEventListener('mouseleave', () => {
            onElementHover({ name: '' });
        });
    });
}